<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const planTable = [
  { plan: 'Starter', mode: 'Tidak bisa mengaktifkan Loyalty sama sekali', color: 'secondary' },
  { plan: 'Pro', mode: 'Boleh pilih SATU mode saja — Poin ATAU Stempel', color: 'info' },
  { plan: 'Premium', mode: 'Boleh aktifkan Poin DAN Stempel sekaligus (mode "Keduanya")', color: 'success' },
]

const faq = [
  {
    q: 'Saya pilih mode "Keduanya" tapi ditolak sistem, kenapa?',
    a: 'Mode "Keduanya" (Poin + Stempel sekaligus) hanya tersedia untuk paket langganan Premium. Kalau toko Anda berlangganan paket Starter atau Pro, pilih salah satu mode saja (Poin atau Stempel), atau hubungi tim PintuKasir untuk upgrade paket.',
  },
  {
    q: 'Poin pelanggan didapat otomatis dari mana?',
    a: 'Poin bertambah otomatis setiap kali pelanggan (yang datanya sudah terhubung ke transaksi) menyelesaikan transaksi — besarnya sesuai "Poin per Rupiah Belanja" yang Anda atur. Anda tidak perlu menambahkan poin manual untuk transaksi normal, cukup pastikan kasir menghubungkan transaksi ke data Customer yang benar saat checkout.',
  },
  {
    q: 'Bedanya "Poin per Rupiah Belanja" dan "Nilai Rupiah per Poin (Tukar)"?',
    a: '"Poin per Rupiah Belanja" mengatur seberapa CEPAT pelanggan MENDAPAT poin dari belanja (mis. 0.01 = tiap Rp10.000 belanja dapat 100 poin). "Nilai Rupiah per Poin (Tukar)" mengatur seberapa BESAR nilai potongan saat poin itu DITUKAR (mis. 100 = tiap 1 poin bernilai potongan Rp100). Keduanya angka yang terpisah dan bisa diatur beda-beda sesuai strategi Anda.',
  },
  {
    q: 'Kenapa tombol "Tukar Poin" tidak muncul di layar kasir?',
    a: 'Ada 3 kemungkinan: (1) belum ada data Customer yang dipilih untuk transaksi ini — poin selalu terikat ke pelanggan tertentu, tidak bisa dipakai anonim, (2) mode Loyalty tenant ini sedang "Nonaktif" atau (3) akun kasir yang login tidak punya izin menukar poin (hubungi Owner untuk cek pengaturan izin di menu User & Role).',
  },
  {
    q: 'Apa itu tab "Saldo Customer" dan kapan saya perlu pakai "Koreksi Saldo Poin"?',
    a: 'Tab ini untuk Owner/Manager mencari pelanggan (lewat nomor HP) dan melihat riwayat lengkap poin/stempelnya. "Koreksi Saldo Poin" dipakai untuk situasi di luar transaksi normal — mis. kompensasi komplain pelanggan (+50 poin sebagai permintaan maaf), atau memperbaiki kesalahan input sebelumnya. Setiap koreksi WAJIB diisi catatan alasannya, dan tercatat permanen di riwayat sebagai jenis "Koreksi Manual" — beda dari poin yang didapat otomatis dari belanja.',
  },
  {
    q: 'Reward Stempel berupa apa saja?',
    a: 'Dua pilihan: "Produk Gratis" (pelanggan dapat 1 produk tertentu gratis setelah stempelnya cukup) atau "Diskon" (potongan nominal Rupiah tetap). Atur jumlah stempel yang dibutuhkan dan pilih reward-nya di tab Pengaturan.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon
            icon="ri-arrow-right-s-line"
            size="16"
            class="text-medium-emphasis"
          />
          <span class="text-body-2">Program Loyalty</span>
        </div>
        <RouterLink to="/userguide">
          <VBtn
            variant="text"
            prepend-icon="ri-arrow-left-line"
            size="small"
            class="mb-4"
          >
            Kembali ke Daftar Modul
          </VBtn>
        </RouterLink>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar
            color="primary"
            variant="tonal"
            size="64"
            rounded="lg"
            class="me-4"
          >
            <VIcon
              icon="ri-vip-diamond-line"
              size="36"
            />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">
              Program Loyalty
            </h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Bikin pelanggan balik lagi lewat program Poin atau Stempel
            </p>
          </div>
        </div>
        <VAlert
          type="info"
          variant="tonal"
        >
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda mengatur Program Loyalty — bedanya mode Poin dan Stempel, cara kasir
          menukarkan poin, dan cara Owner mengoreksi saldo pelanggan secara manual.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-book-open-line"
              class="me-2"
              color="primary"
            />
            Poin vs Stempel — Apa Bedanya?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Program Loyalty</strong> adalah cara memberi penghargaan ke pelanggan yang
              sering belanja di toko Anda, supaya mereka terdorong balik lagi. Ada dua mode
              yang bisa dipilih:
            </p>
            <VRow>
              <VCol
                cols="12"
                sm="6"
              >
                <VCard
                  variant="tonal"
                  color="info"
                  class="h-100"
                >
                  <VCardText class="pa-4">
                    <div class="d-flex align-center gap-2 mb-2">
                      <VIcon
                        icon="ri-coins-line"
                        size="22"
                      />
                      <strong class="text-body-1">Mode Poin</strong>
                    </div>
                    <p class="text-body-2 mb-0">
                      Setiap belanja, pelanggan otomatis dapat sejumlah poin (sesuai rate yang
                      Anda atur). Poin ini bisa <strong>ditukar</strong> kapan saja sebagai potongan
                      harga di transaksi berikutnya — mirip cashback bertahap.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol
                cols="12"
                sm="6"
              >
                <VCard
                  variant="tonal"
                  color="success"
                  class="h-100"
                >
                  <VCardText class="pa-4">
                    <div class="d-flex align-center gap-2 mb-2">
                      <VIcon
                        icon="ri-stack-line"
                        size="22"
                      />
                      <strong class="text-body-1">Mode Stempel</strong>
                    </div>
                    <p class="text-body-2 mb-0">
                      Mirip kartu stempel kertas tradisional ("beli 10 gratis 1") — tiap transaksi
                      dapat 1 stempel, dan setelah jumlah stempel tertentu terkumpul, pelanggan
                      otomatis berhak dapat reward (produk gratis atau diskon).
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Batasan paket -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard
          color="warning"
          variant="tonal"
        >
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-vip-crown-line"
              class="me-2"
            />
            📦 Mode yang Tersedia Tergantung Paket Langganan
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Fitur Loyalty dibatasi sesuai paket langganan toko Anda. Pembatasan ini ditegakkan
              langsung oleh sistem (bukan cuma disembunyikan di layar) — kalau Anda mencoba
              memilih mode yang tidak termasuk paket Anda, sistem akan menolak dan menampilkan
              pesan penjelasannya.
            </p>
            <VTable density="compact">
              <thead>
                <tr>
                  <th>Paket</th>
                  <th>Mode yang Boleh Diaktifkan</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in planTable"
                  :key="row.plan"
                >
                  <td>
                    <VChip
                      :color="row.color"
                      size="small"
                      variant="tonal"
                    >
                      {{ row.plan }}
                    </VChip>
                  </td>
                  <td class="text-body-2 py-2">
                    {{ row.mode }}
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Atur -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-play-circle-line"
              class="me-2"
              color="success"
            />
            Cara Mengatur Program Loyalty
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-0">
              <li class="mb-3">
                Buka menu <strong>Marketing → Program Loyalty</strong> dari sidebar kiri —
                Anda akan berada di tab <strong>"Pengaturan"</strong>.
              </li>
              <li class="mb-3">
                Pilih <strong>Mode Loyalty</strong>: Nonaktif, Poin, Stempel, atau Poin &amp; Stempel
                (Keduanya) — sesuai paket langganan Anda (lihat tabel di atas).
              </li>
              <li class="mb-3">
                Kalau memilih mode <strong>Poin</strong>, isi:
                <ul class="mt-2 mb-2">
                  <li>
                    <strong>Poin per Rupiah Belanja</strong> — mis. 0.01 berarti tiap Rp10.000
                    belanja pelanggan dapat 100 poin.
                  </li>
                  <li>
                    <strong>Nilai Rupiah per Poin (Tukar)</strong> — mis. 100 berarti 1 poin
                    senilai potongan Rp100 saat ditukar.
                  </li>
                  <li>
                    <strong>Minimal Penukaran</strong> (opsional) — jumlah poin minimum yang
                    boleh ditukar dalam satu transaksi.
                  </li>
                </ul>
              </li>
              <li class="mb-3">
                Kalau memilih mode <strong>Stempel</strong>, isi:
                <ul class="mt-2 mb-2">
                  <li><strong>Jumlah Stempel untuk Reward</strong> — mis. 10 stempel baru dapat hadiah.</li>
                  <li><strong>Tipe Reward</strong> — Produk Gratis atau Diskon nominal Rupiah.</li>
                </ul>
              </li>
              <li class="mb-0">
                Klik <strong>"Simpan Pengaturan"</strong>. Perubahan langsung berlaku untuk
                transaksi berikutnya.
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Kasir Tukar Poin -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-store-2-line"
              class="me-2"
              color="info"
            />
            Cara Kasir Menukar Poin Saat Checkout
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-3">
                Di layar <strong>POS Kasir</strong>, pastikan transaksi ini sudah terhubung ke
                <strong>data Customer</strong> — klik ikon pencarian di kolom nama pelanggan untuk
                cari nomor HP-nya, atau daftarkan customer baru kalau belum ada.
              </li>
              <li class="mb-3">
                Setelah customer terpilih, panel keranjang otomatis menampilkan
                <strong>Saldo Poin</strong> pelanggan tersebut (dan batas minimal penukaran
                kalau ada).
              </li>
              <li class="mb-3">
                Ketik jumlah poin yang ingin ditukar di kolom <strong>"Tukar poin"</strong>, atau
                klik tombol <strong>"Maks"</strong> untuk langsung memakai seluruh saldo poin.
              </li>
              <li class="mb-0">
                Ringkasan belanja otomatis menampilkan baris <strong>"Potongan Poin"</strong>
                sesuai jumlah yang ditukar. Lanjutkan checkout seperti biasa.
              </li>
            </ol>
            <VAlert
              type="warning"
              variant="tonal"
              class="mb-0 text-body-2"
            >
              Kolom "Tukar poin" hanya muncul kalau sudah ada Customer yang dipilih DAN mode
              Loyalty toko sedang aktif untuk Poin. Poin <strong>tidak bisa</strong> ditukar
              untuk transaksi tanpa data pelanggan.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Koreksi Saldo -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-edit-2-line"
              class="me-2"
              color="warning"
            />
            Cara Owner Koreksi Saldo Poin Manual
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-3">
                Buka menu <strong>Marketing → Program Loyalty</strong>, lalu pindah ke tab
                <strong>"Saldo Customer"</strong>.
              </li>
              <li class="mb-3">
                Cari pelanggan lewat kolom <strong>"Cari nomor HP customer"</strong>, lalu klik
                namanya dari hasil pencarian.
              </li>
              <li class="mb-3">
                Anda akan melihat saldo Poin, Stempel, total belanja, dan riwayat lengkap
                transaksi loyalty pelanggan tersebut.
              </li>
              <li class="mb-3">
                Klik <strong>"Koreksi Saldo Poin"</strong>, isi <strong>Jumlah</strong> (angka
                positif untuk menambah, negatif untuk mengurangi — mis. <code>-50</code>) dan
                <strong>Catatan</strong> alasannya (wajib diisi).
              </li>
              <li class="mb-0">
                Klik <strong>"Simpan"</strong>. Koreksi langsung tercatat di riwayat sebagai
                jenis <strong>"Koreksi Manual"</strong>, terpisah dari poin hasil belanja normal.
              </li>
            </ol>
            <VAlert
              type="info"
              variant="tonal"
              class="mb-0 text-body-2"
            >
              Gunakan fitur ini untuk situasi di luar kebiasaan — mis. kompensasi komplain,
              memperbaiki salah input, atau hadiah khusus. Untuk poin dari belanja normal,
              biarkan sistem menghitungnya otomatis, tidak perlu dikoreksi manual.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel
            v-for="(item, i) in faq"
            :key="i"
          >
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">
                {{ item.a }}
              </p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Promo & Diskon', sub: 'Diskon otomatis tanpa perlu kode', icon: 'ri-price-tag-3-line', color: 'primary', to: '/userguide/promo-voucher-loyalty/promo' },
              { title: 'Voucher & Kupon', sub: 'Diskon lewat kode, bukan otomatis', icon: 'ri-coupon-3-line', color: 'info', to: '/userguide/promo-voucher-loyalty/voucher' },
              { title: 'Gabungan di 1 Transaksi', sub: 'Promo + Voucher + Poin bareng-bareng', icon: 'ri-git-merge-line', color: 'success', to: '/userguide/promo-voucher-loyalty/kombinasi' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink
              :to="link.to"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar
                    :color="link.color"
                    variant="tonal"
                    size="40"
                    rounded
                  >
                    <VIcon
                      :icon="link.icon"
                      size="22"
                    />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">
                      {{ link.title }}
                    </p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      {{ link.sub }}
                    </p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>